import {
  ChangeDetectorRef,
  Directive,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import {
  BaseComponent,
  EventBusService,
  NetworkService,
  WindowService,
} from '@ups/xplat/core';
import { filter, takeUntil } from 'rxjs';
import { DynamicEventBusTypes } from '../../../utils';
import { DynamicRenderService } from '../../../services';
import {
  dynamicTypesUnsupportedOffline,
  IDynamicConflictState,
  IDynamicModelProperties,
} from '../../../models';

@Directive()
export abstract class DynamicHelperLabelBaseComponent
  extends BaseComponent
  implements OnInit
{
  @Input()
  config: IDynamicModelProperties;
  @Input()
  dynamicService?: DynamicRenderService;

  /**
   * Conflicts this label participates in, keyed (by index) per target control. A control can be a
   * source for several targets, so it can hold more than one at once — one icon, popover lists all.
   * Shared web + NativeScript.
   */
  conflictStates: IDynamicConflictState[] = [];
  /** Whether the inline resolution popover is open (web). */
  showConflictPopover = false;

  private _origInstruction = '0';
  private _wasOffline: boolean;
  private _listeningToNetwork: boolean;

  private win = inject(WindowService);
  private eventBus = inject(EventBusService);
  private network = inject(NetworkService);
  // WI 31903: conflictStates / showConflictPopover and config.instruction are written from the
  // conflictUpdate$ and offline$ subscriptions below, not from a gesture on this view, so neither
  // dirties the OnPush web label nor schedules a zoneless pass. Each write marks the view.
  // Optional for a label built outside a view.
  protected cdr = inject(ChangeDetectorRef, { optional: true });

  ngOnInit() {
    this._listenForConflicts();
    if (this.config) {
      if (
        !this.dynamicService?.activeFormResponse &&
        !this.win.navigator.onLine
      ) {
        // already offline, include note
        this._addOfflineNote();
      }
      if (dynamicTypesUnsupportedOffline.includes(this.config.type)) {
        // listen for network changes to adjust note as state changes
        this._updateNoteWhenNetworkChanges();
      }
    }
  }

  /** Whether to show the conflict icon at all. */
  get hasConflicts(): boolean {
    return this.conflictStates.length > 0;
  }

  /** Bootstrap text-color class for the single icon, by worst severity across all conflicts:
   * red (any unresolved + required), else yellow (any unresolved), else gray (all resolved). */
  get conflictIconClass(): string {
    if (!this.conflictStates.length) {
      return '';
    }
    const unresolved = this.conflictStates.filter(
      (c) => c.status === 'unresolved'
    );
    if (!unresolved.length) {
      return 'text-secondary';
    }
    return unresolved.some((c) => c.required) ? 'text-danger' : 'text-warning';
  }

  /**
   * Human phrase naming the source control(s) driving a conflict's default — e.g.
   * `CO# and Customer`. Uses the full `sourceLabels` combination when present, else falls back to
   * the single trigger (sourceLabel). Empty when unknown.
   */
  conflictSourcesSummary(c: IDynamicConflictState): string {
    const labels = (
      c?.sourceLabels?.length
        ? c.sourceLabels
        : c?.sourceLabel
          ? [c.sourceLabel]
          : []
    ).filter((label): label is string => !!label);
    if (labels.length <= 1) {
      return labels[0] ?? '';
    }
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  }

  toggleConflictPopover() {
    this.showConflictPopover = !this.showConflictPopover;
  }

  /** User picked keep / switch-to-default for a specific target's conflict (from the popover / dialog). */
  resolveConflict(targetFormControlName: string, choice: 'default' | 'keep') {
    if (targetFormControlName) {
      this.dynamicService?.resolveConflict(targetFormControlName, choice);
    }
    this.showConflictPopover = false;
  }

  removeItem() {
    let name: string;
    let index = 0;
    if (this.config.formControlName.startsWith('buttongroup')) {
      name = 'buttongroup';
      index =
        parseInt(
          this.config.formControlName.replace('buttongroupLabel', ''),
          10
        ) - 1;
    } else if (this.config.formControlName.startsWith('updateOnValueChange')) {
      name = 'updateOnValueChange';
      const nameParts = this.config.formControlName.split('-');
      if (nameParts.length === 3) {
        // get index off the end of the formControlName
        index = +nameParts.slice(-1)[0];
      }
    }
    this.eventBus.emit(DynamicEventBusTypes.dynamicRemoveItemFromOptions, {
      config: this.config,
      name,
      index,
    });
  }

  private _listenForConflicts() {
    if (!this.dynamicService || !this.config?.formControlName) {
      return;
    }
    this.dynamicService.conflictUpdate$
      .pipe(
        filter((m) => m.formControlName === this.config.formControlName),
        takeUntil(this.destroy$)
      )
      .subscribe((m) => {
        const targetName =
          m.targetFormControlName ?? m.state?.targetFormControlName;
        if (m.type === 'raise' && m.state) {
          // upsert by target — a re-raise for the same target replaces its entry (new differing default)
          this.conflictStates = [
            ...this.conflictStates.filter(
              (c) => c.targetFormControlName !== m.state.targetFormControlName
            ),
            { ...m.state, status: 'unresolved' },
          ];
        } else if (m.type === 'resolve') {
          // keep the (now gray) icon visible — the user may change their mind until save/cancel
          this.conflictStates = this.conflictStates.map((c) =>
            c.targetFormControlName === targetName
              ? { ...c, status: 'resolved' }
              : c
          );
          if (!this.conflictStates.length) {
            this.showConflictPopover = false;
          }
        } else if (m.type === 'clear') {
          this.conflictStates = this.conflictStates.filter(
            (c) => c.targetFormControlName !== targetName
          );
          if (!this.conflictStates.length) {
            this.showConflictPopover = false;
          }
        }
        this.cdr?.markForCheck();
      });
  }

  private _addOfflineNote() {
    /**
     * Some types are not available for use while offline.
     * This will add a note in the instruction to make clear.
     */
    if (this._origInstruction === '0') {
      this._origInstruction = this.config.instruction;
    }
    switch (this.config.type) {
      case 'file': {
        const offlineNote = `Note: You can only attach files while online.`;
        if (
          !this.config.instruction ||
          this.config.instruction?.indexOf(offlineNote) === -1
        ) {
          this.config.instruction =
            (this.config.instruction ? `${this.config.instruction} ` : '') +
            offlineNote;
        }
        this._updateNoteWhenNetworkChanges();
        break;
      }
    }
  }

  private _updateNoteWhenNetworkChanges() {
    if (!this._listeningToNetwork) {
      this._listeningToNetwork = true;
      this.network.offline$
        .pipe(takeUntil(this.destroy$))
        .subscribe((offline) => {
          if (this.config) {
            if (offline) {
              this._addOfflineNote();
            } else if (
              this._wasOffline &&
              !offline &&
              this._origInstruction !== '0'
            ) {
              // update instruction back to original
              this.config.instruction = this._origInstruction;
            }
          }
          this._wasOffline = offline;
          this.cdr?.markForCheck();
        });
    }
  }
}
